import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { AnimatedRosette } from "./Logo";

const LINES = ["DESPACHO", "ADUANEIRO", "DE PRECISÃO"];
const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yRosette = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: yImage }}
        className="absolute bottom-0 right-0 top-16 hidden w-[46%] overflow-hidden border-l border-ink/10 lg:block"
        aria-hidden="true"
      >
        <img
          src="https://images.pexels.com/photos/13301321/pexels-photo-13301321.jpeg?auto=compress&cs=tinysrgb&w=1800"
          alt=""
          className="h-[115%] w-full object-cover brightness-[0.9] contrast-110 grayscale"
          data-testid="hero-image"
        />
      </motion.div>

      <motion.div
        style={{ y: yRosette }}
        className="pointer-events-none absolute right-[36%] top-[24%] hidden bg-navy p-10 lg:block"
      >
        <AnimatedRosette className="w-40 text-white xl:w-52" />
      </motion.div>

      <div className="relative z-10 px-6 pb-12 lg:px-12 lg:pb-16 lg:max-w-[54%]">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-ink/60"
          data-testid="hero-eyebrow"
        >
          JVF Assessoria e Despachos Aduaneiros LTDA — Rio Grande do Sul
        </motion.p>

        <h1 className="font-oswald font-bold uppercase leading-[0.92] tracking-tight" data-testid="hero-heading">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="block text-[clamp(3rem,9vw,8rem)]"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="max-w-md text-base font-light leading-relaxed text-ink/70 lg:text-lg"
            data-testid="hero-subtitle"
          >
            Importação, exportação e assessoria aduaneira sem burocracia. A sua carga
            atravessa fronteiras — nós cuidamos de todo o resto.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
          >
            <a
              href="#contato"
              data-testid="hero-contact-btn"
              className="inline-block bg-navy px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-ink"
            >
              Fale com a JVF
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative z-10 flex items-center gap-3 border-t border-ink/15 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50 lg:px-12"
        data-testid="hero-scroll-hint"
      >
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={13} />
        </motion.span>
        Role para explorar
      </motion.div>
    </section>
  );
};

export default Hero;
